import moment from 'moment-timezone';
import { brandConfig } from '../../config';
import { endOfDayInBrandTZ, startOfDayInBrandTZ } from '../../utils/formatTime';
/**
 * Timeshare status values from the API
 * Maps from internal Salesforce stages to owner-facing statuses
 *
 * | Status               | Internal Stage(s)         | Owner Actions                 |
 * | -------------------- | ------------------------- | ----------------------------- |
 * | UNLISTED             | Pending, Closed Withdrawn | Update pricing/amount, List   |
 * | PENDING_AGREEMENT    | Pending Agreement         | Sign Agreement                |
 * | PENDING_CONFIRMATION | Pending Confirmation      | Update pricing/amount, View   |
 * | PENDING_VERIFICATION | Pending Verification      | Update pricing/amount, View   |
 * | LISTED               | Listed                    | Update pricing/amount         |
 * | CONFIRMED            | Confirmed                 | View only                     |
 * | RENTED               | Closed Won                | View only                     |
 * | UNRENTED             | Closed Lost               | View only                     |
 */
export type TimeshareStatus =
  | 'UNLISTED'
  | 'PENDING_AGREEMENT'
  | 'PENDING_CONFIRMATION'
  | 'PENDING_VERIFICATION'
  | 'LISTED'
  | 'CONFIRMED'
  | 'RENTED'
  | 'UNRENTED';

export type TimeshareType = 'HOME_RESORT' | 'POINTS_EXCHANGE';

export const TIMESHARE_TYPE_CONFIG: Record<TimeshareType, { label: string; detail: string }> = {
  HOME_RESORT: { label: 'Home Resort', detail: 'This is the property I own at.' },
  POINTS_EXCHANGE: {
    label: 'Points / Exchange',
    detail: 'Vacation club or exchange points were used to book this reservation.'
  }
};

function normalizeTimeshareStatus(rawStatus: unknown): TimeshareStatus {
  if (typeof rawStatus !== 'string') return 'UNLISTED';

  if (rawStatus in TimeshareStatusConfig) {
    return rawStatus as TimeshareStatus;
  }

  return 'UNLISTED';
}

/**
 * Status display configuration for UI labels and colors
 */
export const TimeshareStatusConfig: Record<
  TimeshareStatus,
  { label: string; color: string; textColor: string; muted: boolean }
> = {
  CONFIRMED: {
    label: 'Confirmed',
    color: 'success.light',
    textColor: 'success.contrastText',
    muted: false
  },
  PENDING_AGREEMENT: {
    label: 'Pending Agreement',
    color: 'error.main',
    textColor: 'error.contrastText',
    muted: false
  },
  PENDING_CONFIRMATION: {
    label: 'Pending Confirmation',
    color: 'warning.light',
    textColor: 'common.white',
    muted: false
  },
  PENDING_VERIFICATION: {
    label: 'Pending Verification',
    color: 'warning.light',
    textColor: 'common.white',
    muted: false
  },
  LISTED: {
    label: 'Listed',
    color: 'secondary.light',
    textColor: 'secondary.contrastText',
    muted: false
  },
  UNLISTED: {
    label: 'Unlisted',
    color: 'secondary.light',
    textColor: 'secondary.contrastText',
    muted: true
  },
  RENTED: {
    label: 'Rented',
    color: 'primary.main',
    textColor: 'primary.contrastText',
    muted: false
  },
  UNRENTED: {
    label: 'Unrented',
    color: 'primary.main',
    textColor: 'primary.contrastText',
    muted: true
  }
};

/**
 * Guest information for confirmed timeshares
 */
export interface TimeshareGuest {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mailing_address?: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

/**
 * Timeshare model - represents a timeshare listing record
 * Called "Listing" in the owner portal UI (distinct from Listing__c which are "Properties")
 */
export class TimeshareGeneral {
  public readonly check_in_ISO: Date;
  public readonly check_out_ISO: Date;
  public readonly close_date_ISO: Date | null;
  public readonly booking_non_cancel_date_ISO: Date | null;
  public readonly sortable_name: string;
  public readonly status_config: {
    label: string;
    color: string;
    textColor: string;
    muted: boolean;
  };

  get check_in(): string {
    return this._check_in;
  }

  get check_out(): string {
    return this._check_out;
  }

  constructor(
    // Identity
    public readonly id: string,
    public readonly owner_id: string,
    public readonly owner_name: string,

    // Account (lessor relationship)
    public readonly account_id: string,
    public readonly account_name: string,

    // Confirmation
    public readonly confirmation_number: string,
    public readonly fixed_unit: boolean,

    // Property Details
    public readonly view: string,
    public readonly view_type: string,
    public readonly view_class: string,
    public readonly view_tier: string,
    public readonly view_orientation: string,
    public readonly bedrooms: number,
    public readonly bathrooms: number,
    public readonly unit_type: string,
    public readonly unit_number: string,

    // Bed Configuration
    public readonly king_beds: number,
    public readonly queen_beds: number,
    public readonly full_beds: number,
    public readonly single_beds: number,
    public readonly max_guests: number,

    // Location
    public readonly resort_id: string,
    public readonly resort_name: string,
    public readonly building_id: string,
    public readonly building_name: string,

    // Dates (as strings from API)
    private readonly _check_in: string,
    private readonly _check_out: string,
    private readonly _close_date: string | null,
    private readonly _last_modified: string,

    // Pricing
    public readonly rental_amount: number,
    public readonly nightly_rate: number,
    public readonly nights: number,

    // Week Info
    public readonly is_float: boolean,
    public readonly week_number: string,

    // Listing reference (for create/update)
    public readonly listing_id: string | null,

    // Status flags
    public readonly is_rented: boolean,
    public readonly status: TimeshareStatus,
    public readonly is_external: boolean,
    public readonly is_closed: boolean,
    public readonly is_future: boolean,
    public readonly is_past: boolean,

    // Pricing Strategy (0-10, where 0 = maximize occupancy, 10 = maximize revenue)
    public readonly pricing_strategy: number,
    public readonly recommended_rate: number | null,
    /** Recommended total rental amount (Recommended_Amount__c). When set, owner can accept to update Amount__c. */
    public readonly recommended_amount: number | null,

    // CA/Verification workflow flags
    public readonly requires_verification: boolean,
    public readonly is_verified: boolean,
    public readonly requires_ca: boolean,
    public readonly has_ca: boolean,

    // Reservation info (only present for CONFIRMED status)
    public readonly reservation_id: string | null,
    public readonly reservation_name: string | null,
    public readonly guest_count: number | null,
    public readonly guest: TimeshareGuest | null,

    // Guest Confirmation fields
    public readonly gc_issued: boolean,
    public readonly gc_issued_date: string | null,
    public readonly gc_requested: boolean,
    public readonly gc_requested_date: string | null,

    // Brand (from resort)
    public readonly brand_name: string | null,

    // Reservation type (home resort vs points/exchange)
    public readonly type: TimeshareType,

    // Booking non-cancel date (raw string from API, YYYY-MM-DD)
    private readonly _booking_non_cancel_date: string | null
  ) {
    // Parse dates. Date-only fields (check_in/out, close_date, non_cancel) are
    // calendar dates in brand TZ; anchor them to brand-TZ midnight so display
    // and sort behave the same for every user. last_modified is a full
    // timestamp from the API so parseISO is correct.
    this.check_in_ISO = _check_in ? startOfDayInBrandTZ(_check_in) : new Date();
    this.check_out_ISO = _check_out ? endOfDayInBrandTZ(_check_out) : new Date();
    this.close_date_ISO = _close_date ? startOfDayInBrandTZ(_close_date) : null;

    this.booking_non_cancel_date_ISO = _booking_non_cancel_date
      ? startOfDayInBrandTZ(_booking_non_cancel_date)
      : null;

    // Sortable name (resort + dates)
    this.sortable_name = `${resort_name || ''} ${_check_in || ''}`.toLowerCase();

    // Status config for UI
    this.status_config = TimeshareStatusConfig[status] || {
      label: status,
      color: 'secondary.light',
      textColor: 'secondary.contrastText',
      muted: true
    };
  }

  /**
   * Check if this timeshare requires signing a Contributor Agreement
   * (PENDING_AGREEMENT status with Sign Agreement action)
   * Not allowed for past listings (view details and renew only)
   */
  get needsCASignature(): boolean {
    if (this.is_past) return false;
    return this.status === 'PENDING_AGREEMENT' && !this.has_ca;
  }

  /**
   * Check if this timeshare has a confirmed reservation that needs Guest Confirmation
   */
  get needsGuestConfirmation(): boolean {
    return this.status === 'CONFIRMED' && this.gc_issued === false && this.guest !== null;
  }

  /**
   * Check if the rental amount can be updated (Update Pricing menu option).
   * Per status table:
   * - UNLISTED: Update pricing/amount
   * - PENDING_CONFIRMATION: Update pricing/amount
   * - PENDING_VERIFICATION: Update pricing/amount
   * - LISTED: Update pricing/amount
   * Not allowed for PENDING_AGREEMENT (Sign Agreement only), CONFIRMED, RENTED, UNRENTED (View only).
   * Not allowed for past listings.
   */
  get canUpdateRate(): boolean {
    if (this.is_past) return false;
    return (
      this.status === 'UNLISTED' ||
      this.status === 'PENDING_CONFIRMATION' ||
      this.status === 'PENDING_VERIFICATION' ||
      this.status === 'LISTED'
    );
  }

  /**
   * Check if this timeshare has a recommended amount set.
   * A recommended amount indicates a suggested total rental price change based on pricing strategy.
   */
  get hasRecommendedAmount(): boolean {
    return this.recommended_amount !== null && this.recommended_amount > 0;
  }

  /**
   * Check if the owner can accept the recommended amount.
   * Requires: recommended amount is set AND the timeshare allows rate updates.
   */
  get canAcceptRecommendedRate(): boolean {
    return this.hasRecommendedAmount && this.canUpdateRate && !this.needsCASignature;
  }

  /**
   * Check if this timeshare can be listed (made live)
   * Per status table: only UNLISTED has "List" action
   * Not allowed for past listings (view details and renew only)
   */
  get canList(): boolean {
    if (this.is_past) return false;
    return this.status === 'UNLISTED';
  }

  /**
   * Check if this timeshare can be unlisted (withdrawn)
   * Note: Per business requirement, LISTED does NOT have Unlist in menu
   * (differs from standard table which allows Unlist for LISTED)
   */
  get canUnlist(): boolean {
    return false;
  }

  /**
   * Check if this timeshare can be renewed (create new for next year)
   */
  get canRenew(): boolean {
    return this.is_past;
  }

  /**
   * Get the display label for the status
   */
  get statusLabel(): string {
    return this.status_config.label;
  }

  /**
   * Get the color for the status badge
   */
  get statusColor(): string {
    return this.status_config.color;
  }

  /**
   * Get guest full name if available
   */
  get guestFullName(): string | null {
    if (!this.guest) return null;
    return `${this.guest.first_name} ${this.guest.last_name}`.trim();
  }

  /**
   * Whether this listing needs verification (PENDING_VERIFICATION and not past)
   */
  get needsVerification(): boolean {
    if (this.is_past) return false;
    return this.status === 'PENDING_VERIFICATION';
  }

  /**
   * Whether this listing is in PENDING_CONFIRMATION and has a non-cancel date.
   * If booking_non_cancel_date is missing, CTA should be hidden entirely.
   */
  get needsPendingConfirmation(): boolean {
    if (this.is_past) return false;
    return this.status === 'PENDING_CONFIRMATION' && this._booking_non_cancel_date !== null;
  }

  /**
   * Whether the non-cancel date has passed in the given timezone.
   * Returns true if today (in timezone) is on or after the non-cancel date.
   */
  isPastNonCancelDate(timezone: string): boolean {
    if (!this._booking_non_cancel_date) return false;
    const todayStr = moment().tz(timezone).format('YYYY-MM-DD');
    return todayStr >= this._booking_non_cancel_date;
  }

  /**
   * Formatted non-cancel date string in brand TZ (e.g. "Mar 7, 2026").
   */
  get formattedNonCancelDate(): string | null {
    if (!this.booking_non_cancel_date_ISO) return null;
    return moment(this.booking_non_cancel_date_ISO).tz(brandConfig.timezone).format('MMM D, YYYY');
  }

  /**
   * Whether this listing has any CTA action required
   */
  get hasActionRequired(): boolean {
    return this.needsVerification || this.needsCASignature || this.needsPendingConfirmation;
  }

  /**
   * Brand-aware source label for proof-forwarding copy.
   * Returns brand_name if available, otherwise "your resort".
   */
  get proofSourceLabel(): string {
    return this.brand_name || 'your resort';
  }
}

/**
 * Helper: read from timeshare root or nested listing so single GET response can fill all form fields (renewal).
 */
function fromTimeshareOrListing(
  jsonRecord: any,
  listing: any,
  key: string,
  fallback: any = ''
): any {
  const v = jsonRecord[key];
  if (v !== undefined && v !== null) return v;
  const lv = listing?.[key];
  if (lv !== undefined && lv !== null) return lv;
  return fallback;
}

/**
 * Factory function to instantiate TimeshareGeneral from API JSON response.
 * Reads property/location fields from both top-level and nested listing so one GET has everything for renewal.
 */
export function InstantiateTimeshareFromJSON(jsonRecord: any): TimeshareGeneral {
  const listing = jsonRecord.listing ?? jsonRecord.Listing__r ?? jsonRecord.listing_record ?? {};
  const view = fromTimeshareOrListing(jsonRecord, listing, 'view') ?? '';
  const viewType = fromTimeshareOrListing(jsonRecord, listing, 'view_type') ?? '';
  const viewClass = fromTimeshareOrListing(jsonRecord, listing, 'view_class') ?? '';
  const viewTier = fromTimeshareOrListing(jsonRecord, listing, 'view_tier') ?? '';
  const viewOrientation = fromTimeshareOrListing(jsonRecord, listing, 'view_orientation') ?? '';
  const bedrooms = jsonRecord.bedrooms ?? listing.bedrooms ?? 0;
  const bathrooms = jsonRecord.bathrooms ?? listing.bathrooms ?? 0;
  const unitType = fromTimeshareOrListing(jsonRecord, listing, 'unit_type') ?? '';
  const unitNumber =
    jsonRecord.unit_number != null && String(jsonRecord.unit_number) !== ''
      ? String(jsonRecord.unit_number)
      : '';
  // Bed configuration
  const kingBeds = jsonRecord.king_beds ?? listing.king_beds ?? 0;
  const queenBeds = jsonRecord.queen_beds ?? listing.queen_beds ?? 0;
  const fullBeds = jsonRecord.full_beds ?? listing.full_beds ?? 0;
  const singleBeds = jsonRecord.single_beds ?? listing.single_beds ?? 0;
  const maxGuests = jsonRecord.max_guests ?? listing.max_guests ?? 0;
  const resortId = fromTimeshareOrListing(jsonRecord, listing, 'resort_id') ?? '';
  const resortName = fromTimeshareOrListing(jsonRecord, listing, 'resort_name') ?? '';
  const buildingId = fromTimeshareOrListing(jsonRecord, listing, 'building_id') ?? '';
  const buildingName = fromTimeshareOrListing(jsonRecord, listing, 'building_name') ?? '';
  const listingId = jsonRecord.listing_id ?? listing.id ?? listing.listing_id ?? null;

  return new TimeshareGeneral(
    jsonRecord.id ?? '', // renewal/listing preview may have id: null
    jsonRecord.owner_id,
    jsonRecord.owner_name,
    jsonRecord.account_id,
    jsonRecord.account_name,
    jsonRecord.confirmation_number ?? '',
    jsonRecord.fixed_unit === true,
    view,
    viewType,
    viewClass,
    viewTier,
    viewOrientation,
    typeof bedrooms === 'number' ? bedrooms : Number(bedrooms) || 0,
    typeof bathrooms === 'number' ? bathrooms : Number(bathrooms) || 0,
    unitType,
    unitNumber,
    typeof kingBeds === 'number' ? kingBeds : Number(kingBeds) || 0,
    typeof queenBeds === 'number' ? queenBeds : Number(queenBeds) || 0,
    typeof fullBeds === 'number' ? fullBeds : Number(fullBeds) || 0,
    typeof singleBeds === 'number' ? singleBeds : Number(singleBeds) || 0,
    typeof maxGuests === 'number' ? maxGuests : Number(maxGuests) || 0,
    resortId,
    resortName,
    buildingId,
    buildingName,
    jsonRecord.check_in ?? '',
    jsonRecord.check_out ?? '',
    jsonRecord.close_date,
    jsonRecord.last_modified,
    jsonRecord.rental_amount ?? 0,
    jsonRecord.nightly_rate ?? 0,
    jsonRecord.nights ?? 0,
    jsonRecord.is_float ?? false,
    jsonRecord.week_number,
    listingId,
    jsonRecord.is_rented ?? false,
    normalizeTimeshareStatus(jsonRecord.status),
    jsonRecord.is_external ?? false,
    jsonRecord.is_closed ?? false,
    jsonRecord.is_future ?? false,
    jsonRecord.is_past ?? false,
    jsonRecord.pricing_strategy ?? 2,
    jsonRecord.recommended_rate,
    jsonRecord.recommended_amount ??
      (jsonRecord.recommended_rate != null && jsonRecord.nights
        ? (jsonRecord.recommended_rate as number) * (jsonRecord.nights as number)
        : null),
    jsonRecord.requires_verification ?? false,
    jsonRecord.is_verified ?? false,
    jsonRecord.requires_ca ?? false,
    jsonRecord.has_ca ?? false,
    jsonRecord.reservation_id,
    jsonRecord.reservation_name,
    jsonRecord.guest_count,
    jsonRecord.guest ?? null,
    jsonRecord.gc_issued ?? false,
    jsonRecord.gc_issued_date,
    jsonRecord.gc_requested ?? false,
    jsonRecord.gc_requested_date,
    jsonRecord.brand_name ?? listing.brand_name ?? null,
    (jsonRecord.type === 'POINTS_EXCHANGE' ? 'POINTS_EXCHANGE' : 'HOME_RESORT') as TimeshareType,
    jsonRecord.booking_non_cancel_date ?? null
  );
}

/**
 * Regions (islands) for listing flow - map of region name to Place ID
 */
export type TimeshareRegionsMap = Record<string, string>;

export interface TimeshareRegionOption {
  id: string;
  name: string;
  listing_count?: number;
}

/**
 * Listing preview from GET /listing - property config before dates/pricing
 */
export interface TimeshareListingPreview {
  id: null;
  listing_id: string;
  listing_name: string;
  resort_id: string;
  resort_name: string;
  resort_area_id?: string;
  resort_area?: string;
  resort_city?: string;
  brand_name?: string;
  building_id?: string;
  building_name?: string;
  bedrooms: number;
  bathrooms: number;
  max_guests?: number;
  view?: string;
  view_type?: string;
  view_class?: string;
  view_tier?: string;
  view_orientation?: string;
  unit_type: string;
  unit_number?: string;
  is_float: boolean;
  broker_rate: number;
  /** Listing photo URL for preview/thumbnail; fallback is "Image coming soon" placeholder */
  listing_photo_url?: string;
  // Bed configuration
  king_beds?: number;
  queen_beds?: number;
  full_beds?: number;
  single_beds?: number;
}

/**
 * Resort option for new listing flow
 */
export interface TimeshareResort {
  id: string;
  name: string;
  brand_name: string;
  description: string;
  locale?: string;
  location: string;
  resort_area_id?: string;
  resort_area?: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  phone: string;
  /** Resort photo URL for preview card; fallback is "Image coming soon" placeholder */
  resort_photo_url?: string;
  /** When true, the listing flow may offer fixed-unit reservation details for this resort */
  has_fixed_units?: boolean;
}

/**
 * Building option for new listing flow
 */
export interface TimeshareBuilding {
  id: string;
  name: string;
  resort_id: string;
}

/**
 * View type options for new listing flow
 */
export interface TimeshareViewTypeOption {
  view_class: string | null;
  tier: string | null;
  orientation: string | null;
}

/**
 * Comparable timeshare for pricing guidance (anonymized)
 */
export interface TimeshareComp {
  view: string;
  bedrooms: number;
  bathrooms: number;
  unit_type: string;
  resort_id: string;
  resort_name: string;
  building_id: string;
  building_name: string;
  check_in: string;
  check_out: string;
  rental_amount: number;
  nightly_rate: number;
  nights: number;
  week_number: string;
  status: TimeshareStatus;
}

/** Bucket name for comp groups: Direct, Location, Area, City */
export type TimeshareCompBucketName = 'Direct' | 'Location' | 'Area' | 'City';

/**
 * One group of comparables from getTimeshareComps (comp_buckets item)
 */
export interface TimeshareCompBucket {
  bucket_name: TimeshareCompBucketName;
  low_nightly_rate: number;
  high_nightly_rate: number;
  count: number;
  avg_nightly_rate?: number;
  is_historical?: boolean;
  comps: TimeshareComp[];
}

/**
 * API response when getTimeshareComps returns comp_buckets
 */
export interface TimeshareCompsWithBucketsResponse {
  status?: number;
  comp_buckets?: TimeshareCompBucket[];
  comps?: TimeshareComp[];
  error?: { code: string; message: string };
}

/**
 * Request body for creating a new timeshare
 */
export interface TimeshareCreateRequest {
  listing_id: string;
  check_in: string;
  check_out: string;
  account_id: string;
  owner_id: string;
  rental_amount?: number;
}

/**
 * Request body for renewing a timeshare
 */
export interface TimeshareRenewRequest {
  id: string; // Source timeshare ID
}

/**
 * Response from renewal preview endpoint
 */
export interface TimeshareRenewPreview {
  owner_id: string;
  owner_name: string;
  account_id: string;
  account_name: string;
  rental_amount: number;
  check_in: string;
  check_out: string;
  nights: number;
  status: TimeshareStatus;
  is_closed: boolean;
  is_future: boolean;
  is_past: boolean;
  view: string;
  unit_type: string;
  unit_number: string;
  is_float: boolean;
  week_number: string;
  bedrooms: number;
  bathrooms: number;
  resort_id: string;
  resort_name: string;
  building_id: string;
  building_name: string;
  is_external: boolean;
  recommended_rate: number;
}

/**
 * API response wrapper for timeshare list endpoint
 */
export interface TimeshareListResponse {
  status: number;
  timeshares: any[];
  error?: {
    code: string;
    message: string;
  };
}

/**
 * API response wrapper for single timeshare endpoint
 */
export interface TimeshareSingleResponse {
  status: number;
  timeshare: any;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * API response wrapper for comps endpoint
 */
export interface TimeshareCompsResponse {
  status: number;
  comps: TimeshareComp[];
  error?: {
    code: string;
    message: string;
  };
}

/**
 * API response for embedded signing URL
 */
export interface TimeshareSigningUrlResponse {
  status: number;
  signing_url: string;
  envelope_id: string;
  expires_in_seconds: number;
  error?: {
    code: string;
    message: string;
  };
}
